import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { useTranslations } from "@/hooks/use-translations";

const MIN_WIDTH = 220;
const MAX_WIDTH = 520;
// Reserve the Studio column and a readable chat.
const RESERVED_WIDTH = 360 + 320;
const STORAGE_KEY = "minora.workspace.resourcesWidth";

function readSavedWidth(): number | null {
	try {
		const saved = window.localStorage.getItem(STORAGE_KEY);
		if (saved === null) return null;
		const value = Number(saved);
		return Number.isFinite(value) && value > 0
			? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(value)))
			: null;
	} catch {
		return null;
	}
}

function saveWidth(width: number | null) {
	try {
		if (width === null) window.localStorage.removeItem(STORAGE_KEY);
		else window.localStorage.setItem(STORAGE_KEY, String(width));
	} catch {
		// Resizing also works when browser storage is unavailable.
	}
}

export function ResizableResourcesPanel({ children }: { children: ReactNode }) {
	const t = useTranslations("workspace");
	const panelId = useId();
	const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
	const [preferredWidth, setPreferredWidth] = useState(readSavedWidth);
	const [dragging, setDragging] = useState(false);
	const dragRef = useRef<{
		pointerId: number;
		startX: number;
		startWidth: number;
		lastWidth: number;
	} | null>(null);
	const maxWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, viewportWidth - RESERVED_WIDTH));
	const defaultWidth = viewportWidth >= 1280 ? 280 : 250;
	const width = Math.max(MIN_WIDTH, Math.min(maxWidth, preferredWidth ?? defaultWidth));

	useEffect(() => {
		const onResize = () => setViewportWidth(window.innerWidth);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		const drag = dragRef.current;
		if (!dragging || !drag) return;
		const previousCursor = document.body.style.cursor;
		const previousUserSelect = document.body.style.userSelect;
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
		const onMove = (event: PointerEvent) => {
			if (event.pointerId !== drag.pointerId) return;
			drag.lastWidth = Math.max(
				MIN_WIDTH,
				Math.min(maxWidth, Math.round(drag.startWidth + event.clientX - drag.startX)),
			);
			setPreferredWidth(drag.lastWidth);
		};
		const onEnd = (event?: PointerEvent) => {
			if (event && event.pointerId !== drag.pointerId) return;
			saveWidth(drag.lastWidth);
			dragRef.current = null;
			setDragging(false);
		};
		const onBlur = () => onEnd();
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onEnd);
		window.addEventListener("pointercancel", onEnd);
		window.addEventListener("blur", onBlur);
		return () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onEnd);
			window.removeEventListener("pointercancel", onEnd);
			window.removeEventListener("blur", onBlur);
			document.body.style.cursor = previousCursor;
			document.body.style.userSelect = previousUserSelect;
		};
	}, [dragging, maxWidth]);

	return (
		<motion.aside
			id={panelId}
			initial={{ opacity: 0, x: -20 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ duration: 0.3, delay: 0.05 }}
			style={{ width }}
			className="relative flex-shrink-0 border border-t-0 border-r-0 rounded-none border-border-subtle bg-bg-surface flex flex-col"
		>
			{children}
			<div
				role="separator"
				tabIndex={0}
				aria-label={t("resizeResources")}
				aria-orientation="vertical"
				aria-controls={panelId}
				aria-valuemin={MIN_WIDTH}
				aria-valuemax={maxWidth}
				aria-valuenow={width}
				aria-valuetext={`${width}px`}
				title={t("resizeResourcesHint")}
				onPointerDown={(event) => {
					if (event.button !== 0 || dragRef.current) return;
					event.preventDefault();
					dragRef.current = {
						pointerId: event.pointerId,
						startX: event.clientX,
						startWidth: width,
						lastWidth: width,
					};
					setDragging(true);
				}}
				onDoubleClick={() => {
					setPreferredWidth(null);
					saveWidth(null);
				}}
				onKeyDown={(event) => {
					let next: number;
					if (event.key === "ArrowLeft") next = width - 16;
					else if (event.key === "ArrowRight") next = width + 16;
					else if (event.key === "Home") next = MIN_WIDTH;
					else if (event.key === "End") next = maxWidth;
					else return;
					event.preventDefault();
					next = Math.max(MIN_WIDTH, Math.min(maxWidth, next));
					setPreferredWidth(next);
					saveWidth(next);
				}}
				className="absolute inset-y-4 -right-2 z-20 w-4 cursor-col-resize touch-none select-none outline-none focus-visible:bg-border-subtle/40"
			/>
		</motion.aside>
	);
}
